import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Download, Mail, Share2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [donation, setDonation] = useState(null);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('No payment session found');
      setLoading(false);
      return;
    }

    const fetchStatus = async (attempts = 0) => {
      try {
        // First try to get donation by session ID
        const donationResponse = await fetch(`${API_URL}/api/donations/by-session/${sessionId}`);
        
        if (donationResponse.ok) {
          const donationData = await donationResponse.json();
          
          // If confirmed, we're done
          if (donationData.donation.status === 'confirmed') {
            setDonation(donationData.donation);
            setLoading(false);
            return;
          }
        }

        // Poll payment status
        const statusResponse = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
        
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          
          if (statusData.payment_status === 'paid') {
            // Wait a bit for fulfillment, then fetch donation again
            setPolling(true);
            
            setTimeout(async () => {
              const finalResponse = await fetch(`${API_URL}/api/donations/by-session/${sessionId}`);
              if (finalResponse.ok) {
                const finalData = await finalResponse.json();
                setDonation(finalData.donation);
              }
              setLoading(false);
              setPolling(false);
            }, 2000);
            return;
          } else if (statusData.status === 'expired') {
            setError('Payment session expired');
            setLoading(false);
            return;
          }
        }

        // Retry if still processing
        if (attempts < 5) {
          setTimeout(() => fetchStatus(attempts + 1), 2000);
        } else {
          // After max attempts, just show what we have
          const finalResponse = await fetch(`${API_URL}/api/donations/by-session/${sessionId}`);
          if (finalResponse.ok) {
            const finalData = await finalResponse.json();
            setDonation(finalData.donation);
          }
          setLoading(false);
        }

      } catch (err) {
        console.error('Error fetching status:', err);
        if (attempts < 3) {
          setTimeout(() => fetchStatus(attempts + 1), 2000);
        } else {
          setError('Failed to load donation details');
          setLoading(false);
        }
      }
    };

    fetchStatus();
  }, [sessionId]);

  const getDonorName = () => {
    if (!donation) return '';
    return donation.individual_data?.name || donation.business_data?.business_name || 'Donor';
  };

  const getDonorEmail = () => {
    if (!donation) return '';
    return donation.individual_data?.email || donation.business_data?.contact_email || '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleShare = async () => {
    const shareText = `I just donated to If You Teach Me Inc.! Join me in supporting education. ${window.location.origin}/donate`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'If You Teach Me Inc. Donation',
          text: shareText,
          url: `${window.location.origin}/donate`,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleDownloadPDF = async () => {
    if (!donation || !sessionId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/donations/${donation._id}/download-receipt?session_id=${sessionId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Convert base64 to blob and download
        const byteCharacters = atob(data.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `receipt_${donation.receipt_number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success('Receipt downloaded!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (err) {
      toast.error('Failed to download receipt');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-teal-breeze py-12">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Card className="shadow-elevated">
            <CardContent className="py-16 text-center">
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">
                {polling ? 'Processing your donation...' : 'Loading donation details...'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-teal-breeze py-12">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Card className="shadow-elevated">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-destructive text-2xl">!</span>
              </div>
              <h2 className="font-[Space_Grotesk] text-xl font-semibold mb-2">Something went wrong</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Link to="/donate">
                <Button>Try Again</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-teal-breeze py-12">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <Card className="shadow-elevated relative overflow-hidden" data-testid="receipt-card">
          {/* Success Badge */}
          <div className="absolute top-4 right-4">
            <Badge className="bg-[hsl(var(--success))] text-white gap-1.5 px-3 py-1.5">
              <Check className="w-4 h-4" />
              {donation?.status === 'confirmed' ? 'Confirmed' : 'Processing'}
            </Badge>
          </div>

          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="If You Teach Me Inc." 
                className="h-20 w-auto"
              />
            </div>
            <h1 className="font-[Space_Grotesk] text-2xl sm:text-3xl font-semibold text-foreground">
              Thank you, {getDonorName()}!
            </h1>
            <p className="text-muted-foreground mt-2">
              Your generous donation helps us expand educational opportunities.
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Receipt Box */}
            <div className="border border-dashed rounded-lg p-6" data-testid="receipt-details">
              <h3 className="font-[Space_Grotesk] font-semibold mb-4 text-center">Donation Receipt</h3>
              
              <div className="space-y-3 font-[Roboto_Mono] text-sm">
                <div className="flex justify-between items-center py-2 border-b border-dashed">
                  <span className="text-muted-foreground">Receipt #</span>
                  <span className="font-medium" data-testid="receipt-number-text">
                    {donation?.receipt_number || 'Processing...'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-dashed">
                  <span className="text-muted-foreground">Date</span>
                  <span data-testid="receipt-date-text">{formatDate(donation?.created_at)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-dashed">
                  <span className="text-muted-foreground">Donor</span>
                  <span>{getDonorName()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-dashed">
                  <span className="text-muted-foreground">Email</span>
                  <span>{getDonorEmail()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-dashed">
                  <span className="text-muted-foreground">Payment</span>
                  <span>Credit Card (Stripe)</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-muted-foreground text-base">Amount</span>
                  <span className="text-2xl font-bold text-primary" data-testid="receipt-total-amount-text">
                    ${donation?.amount?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>

            {/* Tax Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="text-amber-800">
                <strong>Tax Deduction Notice:</strong> If You Teach Me Inc. is a 501(c)(3) tax-exempt organization 
                (EIN: 33-2088658). Your donation is tax-deductible to the fullest extent allowed by law. 
                No goods or services were provided in exchange for this contribution.
              </p>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                className="flex-1 btn-lift" 
                data-testid="download-receipt-button" 
                onClick={handleDownloadPDF}
                disabled={!donation?.receipt_number}
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button 
                variant="outline" 
                className="flex-1" 
                data-testid="email-receipt-button" 
                disabled
                title="Receipt was automatically emailed"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Sent ✓
              </Button>
              <Button variant="secondary" className="flex-1" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              A receipt has been sent to {getDonorEmail()}
            </p>

            <Separator />

            {/* Footer */}
            <div className="text-center space-y-4">
              <Link to="/donate">
                <Button variant="outline" size="lg">
                  Make Another Donation
                </Button>
              </Link>
              
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium">If You Teach Me Inc.</p>
                <p>501(c)(3) • EIN: 33-2088658</p>
                <p>2740 East WT Harris Blvd Ste 200, Charlotte, NC 28213</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
